import { useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import { useMeters, useFrequency } from "../hooks/useMeters";
import {
  PageHeader,
  ChartCard,
  MeterSelect,
  DaysSelect,
  Spinner,
  StatCard,
} from "../components/common";
import { fmt, CHART_COLORS } from "../utils/helpers";
import Layout from "../../../layout";
import "../reportAnalytics.css";

export default function FrequencyPage() {
  const { data: meterList } = useMeters();
  const meters = meterList?.meters ?? [];
  const [meterId, setMeterId] = useState(meters[0] ?? "");
  const [days, setDays] = useState(90);

  const { data, isLoading } = useFrequency(meterId || meters[0], days);
  const points = data?.data ?? [];

  const deviations = points.map((p) => Math.abs(p.deviation ?? 0));
  const maxDev = deviations.length ? Math.max(...deviations) : 0;
  const avgFreq = points.length
    ? points.reduce((s, p) => s + (p.frequency ?? 50), 0) / points.length
    : 50;

  const chartData = points.map((p) => ({
    ts: fmt.dt(p.timestamp),
    frequency: p.frequency,
    deviation: p.deviation,
  }));

  return (
    <Layout>
      <div className="p-0.5">
        <PageHeader
          title="Frequency Stability"
          subtitle="Grid frequency deviation from 50 Hz nominal"
        >
          <MeterSelect
            meters={meters}
            value={meterId || meters[0]}
            onChange={setMeterId}
          />
          <DaysSelect value={days} onChange={setDays} />
        </PageHeader>

        <div className="grid grid-cols-3 gap-4 mb-6">
          <StatCard
            label="Avg Frequency"
            value={fmt.num(avgFreq, 3)}
            unit="Hz"
            color="blue"
          />
          <StatCard
            label="Max Deviation"
            value={fmt.num(maxDev, 3)}
            unit="Hz"
            color="amber"
          />
          <StatCard label="Readings" value={points.length} color="violet" />
        </div>

        <ChartCard
          title="Frequency Over Time"
          subtitle="Nominal 50.0 Hz · tolerance ±0.5 Hz"
        >
          {isLoading ? (
            <Spinner />
          ) : (
            <ResponsiveContainer width="100%" height={320}>
              <LineChart
                data={chartData}
                margin={{ top: 8, right: 16, left: 0, bottom: 8 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={CHART_COLORS.grid}
                />
                <XAxis
                  dataKey="ts"
                  tick={{ fill: "#64748B", fontSize: 11 }}
                  interval="preserveStartEnd"
                  tickLine={false}
                />
                <YAxis
                  domain={[49, 51]}
                  tick={{ fill: "#64748B", fontSize: 11 }}
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip
                  content={({ active, payload, label }: any) => {
                    if (!active || !payload?.length) return null;
                    return (
                      <div className="ra-tooltip">
                        <p className="text-slate-400 mb-1">{label}</p>
                        <p className="font-mono text-cyan-600">
                          Freq: {payload[0]?.value?.toFixed(3)} Hz
                        </p>
                      </div>
                    );
                  }}
                />
                <ReferenceLine
                  y={50.5}
                  stroke={CHART_COLORS.red}
                  strokeDasharray="4 2"
                />
                <ReferenceLine
                  y={50.0}
                  stroke={CHART_COLORS.slate}
                  strokeDasharray="4 2"
                />
                <ReferenceLine
                  y={49.5}
                  stroke={CHART_COLORS.amber}
                  strokeDasharray="4 2"
                />
                <Line
                  type="monotone"
                  dataKey="frequency"
                  stroke={CHART_COLORS.cyan}
                  dot={false}
                  strokeWidth={1.5}
                  name="Frequency"
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </div>
    </Layout>
  );
}
